import React, { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { MapPin, User, Plus, LogOut, Settings } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'

export default function Navigation() {
  const location = useLocation()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const { user, signOut } = useAuth()
  const userMenuRef = useRef<HTMLDivElement>(null)
  const userButtonRef = useRef<HTMLButtonElement>(null)
  
  const handleSignOut = async () => {
    await signOut()
    setShowUserMenu(false)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setShowUserMenu(false)
      userButtonRef.current?.focus()
    }
  }

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserMenu])
  
  return (
    <nav
      id="navigation"
      className="bg-white border-b border-neutral-200"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="flex justify-between items-center h-18">
          <div className="flex items-center space-x-10">
            <Link
              to="/"
              className="flex items-center space-x-3 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg p-2 transition-colors hover:bg-neutral-50"
              aria-label="Ultra Plan - Go to homepage"
            >
              <div className="p-1.5 bg-primary-700 rounded-lg">
                <MapPin className="h-5 w-5 text-white" aria-hidden="true" />
              </div>
              <span className="text-xl font-bold text-primary-900">UltraPlan</span>
            </Link>

            <div className="hidden md:flex space-x-1" role="menubar">
              <Link
                to="/"
                role="menuitem"
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  location.pathname === '/'
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-neutral-600 hover:text-primary-700 hover:bg-neutral-50'
                }`}
                aria-current={location.pathname === '/' ? 'page' : undefined}
              >
                Dashboard
              </Link>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <button
              className="flex items-center space-x-2 bg-primary-700 text-white px-5 py-2.5 rounded-lg hover:bg-primary-800 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 font-medium text-sm shadow-minimal"
              aria-label="Create new run"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">New Run</span>
            </button>

            <div className="relative" ref={userMenuRef}>
              <button
                ref={userButtonRef}
                onClick={() => setShowUserMenu(!showUserMenu)}
                onKeyDown={handleKeyDown}
                className="p-2.5 text-neutral-500 hover:text-primary-700 hover:bg-neutral-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg"
                aria-label="User menu"
                aria-expanded={showUserMenu}
                aria-haspopup="true"
              >
                <User className="h-5 w-5" aria-hidden="true" />
              </button>

              {showUserMenu && (
                <div
                  className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-minimal-lg border border-neutral-200 py-2 z-50 focus:outline-none animate-fade-in"
                  role="menu"
                  aria-orientation="vertical"
                  onKeyDown={handleKeyDown}
                >
                  <div
                    className="px-4 py-3 text-sm text-neutral-700 border-b border-neutral-100 font-medium"
                    role="menuitem"
                    tabIndex={-1}
                  >
                    {user?.email}
                  </div>
                  <Link
                    to="/settings"
                    className="flex items-center w-full px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 focus:bg-neutral-50 focus:outline-none transition-colors"
                    role="menuitem"
                    onClick={() => setShowUserMenu(false)}
                  >
                    <Settings className="h-4 w-4 mr-3 text-neutral-400" aria-hidden="true" />
                    Settings
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="flex items-center w-full px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 focus:bg-neutral-50 focus:outline-none transition-colors"
                    role="menuitem"
                  >
                    <LogOut className="h-4 w-4 mr-3 text-neutral-400" aria-hidden="true" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  )
}